import rootStore from '@vue-storefront/core/store'
import guidedCheckoutConfig from 'theme/config/guided-checkout'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle'
import { SelectedBundleOption } from '@vue-storefront/core/modules/catalog/types/BundleOption'
import { getProductPrice } from 'theme/helpers'

export function isGuidedCheckoutEnabled (product) {
  return Boolean(
    guidedCheckoutConfig.enabled &&
    product &&
    product.bundle_options &&
    product.bundle_options.length > 0
  )
}

export function getSortedBundleOptions (product) {
  return (product.bundle_options || []).slice().sort((a, b) => a.position - b.position)
}

export function getMainBundleOption (product) {
  return getSortedBundleOptions(product)[0]
}

export function getAdditionalBundleOptions (product) {
  const main = getMainBundleOption(product)
  if (!main) return []
  return getSortedBundleOptions(product).filter(option => option.option_id !== main.option_id)
}

export function findBundleOptionByKeywords (product, keywords = []) {
  const normalizedKeywords = keywords.map(keyword => keyword.toLowerCase())

  return getAdditionalBundleOptions(product).find(option => {
    const title = (option.title || '').toLowerCase()
    return normalizedKeywords.some(keyword => title.includes(keyword))
  })
}

export function getSkirtBundleOption (product) {
  return findBundleOptionByKeywords(product, ['leiste', 'leisten', 'sockel'])
}

export function getUnderlayBundleOption (product) {
  return findBundleOptionByKeywords(product, ['unterlage', 'dämmung', 'daemmung', 'tritt'])
}

export function isBundleOptionSelectable (option) {
  if (!option) return false
  return option.product_links.length > 1 || !option.required
}

export function findProductLinkByKeywords (option, keywords = []) {
  if (!option || !option.product_links) return null

  const normalizedKeywords = keywords.map(keyword => keyword.toLowerCase())

  return option.product_links.find(link => {
    const name = ((link.product && link.product.name) || '').toLowerCase()
    return normalizedKeywords.some(keyword => name.includes(keyword))
  })
}

export function findDefaultProductLink (option) {
  if (!option || !option.product_links) return null
  return option.product_links.find(link => link.is_default) || option.product_links[0]
}

export function findPremiumProductLink (option, keywords = []) {
  if (!option || !option.product_links) return null

  const keywordMatch = findProductLinkByKeywords(option, keywords)
  if (keywordMatch) return keywordMatch

  return option.product_links.slice().sort((a, b) => {
    return Number(b.price || 0) - Number(a.price || 0)
  })[0]
}

export function getBundleOptionRef (bundleOptionsComponent, option) {
  if (!bundleOptionsComponent || !option) return null
  const refs = bundleOptionsComponent.$refs['bundleOption_' + option.option_id]
  return refs && refs[0] ? refs[0] : null
}

export function selectBundleOptionLink (bundleOptionsComponent, option, link) {
  const optionComponent = getBundleOptionRef(bundleOptionsComponent, option)
  if (!optionComponent || !link) return

  // Match classic radio input coercion so ProductBundleOption.value lookup works
  const nextId = link.id === '' || link.id == null ? null : Number(link.id)
  if (optionComponent.productOptionId === nextId) return

  optionComponent.productOptionId = nextId
}

export function clearBundleOptionSelection (bundleOptionsComponent, option) {
  const optionComponent = getBundleOptionRef(bundleOptionsComponent, option)
  if (!optionComponent) return

  if (optionComponent.productOptionId == null) return
  optionComponent.productOptionId = null
}

export function setMainBundleQuantity (bundleOptionsComponent, product, quantity) {
  const optionComponent = getBundleOptionRef(bundleOptionsComponent, getMainBundleOption(product))
  if (!optionComponent) return

  optionComponent.quantity = quantity
  syncAdditionalBundleQuantities(bundleOptionsComponent)
}

function optionWithPresetLinks (option, preset) {
  if (!option) return null
  return {
    ...option,
    product_links: filterBundleLinksForPreset(option.product_links || [], preset)
  }
}

export function applyPackagePreset (bundleOptionsComponent, product, preset) {
  const skirtOption = getSkirtBundleOption(product)
  const underlayOption = getUnderlayBundleOption(product)
  const premiumKeywords = guidedCheckoutConfig.presets.premium

  if (preset === 'einzelprodukt') {
    if (skirtOption) {
      clearBundleOptionSelection(bundleOptionsComponent, skirtOption)
    }

    if (underlayOption) {
      clearBundleOptionSelection(bundleOptionsComponent, underlayOption)
    }

    return
  }

  if (preset === 'premium') {
    if (skirtOption) {
      const premiumSkirt = findPremiumProductLink(
        optionWithPresetLinks(skirtOption, 'premium'),
        premiumKeywords.skirtKeywords
      )
      selectBundleOptionLink(bundleOptionsComponent, skirtOption, premiumSkirt)
    }

    if (underlayOption) {
      const premiumUnderlay = findPremiumProductLink(
        optionWithPresetLinks(underlayOption, 'premium'),
        premiumKeywords.underlayKeywords
      )
      selectBundleOptionLink(bundleOptionsComponent, underlayOption, premiumUnderlay)
    }

    syncAdditionalBundleQuantities(bundleOptionsComponent)
    return
  }

  if (skirtOption) {
    selectBundleOptionLink(
      bundleOptionsComponent,
      skirtOption,
      findDefaultProductLink(optionWithPresetLinks(skirtOption, 'standard'))
    )
  }

  if (underlayOption) {
    selectBundleOptionLink(
      bundleOptionsComponent,
      underlayOption,
      findDefaultProductLink(optionWithPresetLinks(underlayOption, 'standard'))
    )
  }

  syncAdditionalBundleQuantities(bundleOptionsComponent)
}

export function syncAdditionalBundleQuantities (bundleOptionsComponent) {
  if (!bundleOptionsComponent || typeof bundleOptionsComponent.configureBundleParts !== 'function') {
    return
  }

  bundleOptionsComponent.configureBundleParts()
}

export function estimateProjectArea (mainOptionValue) {
  if (!mainOptionValue || !mainOptionValue.product) return 0
  const product = mainOptionValue.product as any
  return (mainOptionValue.qty || 0) * (product.baseprice_product_amount || 0)
}

export function estimateSkirtMeters (product, mainOptionValue, metersPerPack = guidedCheckoutConfig.skirtMetersPerPack) {
  const skirtOption = getSkirtBundleOption(product)
  if (!skirtOption) return 0

  const skirtValue = getSelectedBundleOptionValue(product, skirtOption)
  if (!skirtValue) return 0

  const packs = skirtValue.qty || 0
  const skirtProduct: any = skirtValue.product
  const packMeters = skirtProduct && skirtProduct.baseprice_product_amount
    ? skirtProduct.baseprice_product_amount
    : metersPerPack

  return packs * packMeters
}

export function getSkirtLinkCategory (link, filterGroups = guidedCheckoutConfig.skirtFilterGroups) {
  const name = ((link.product && link.product.name) || '').toLowerCase()
  const categoryOrder = guidedCheckoutConfig.skirtCategoryOrder || ['clip', 'fuxx', 'sockel']

  for (const id of categoryOrder) {
    const group = filterGroups.find(entry => entry.id === id)
    if (!group || !group.keywords) continue
    if (group.keywords.some(keyword => name.includes(String(keyword).toLowerCase()))) {
      return id
    }
  }

  return 'weitere'
}

export function productLinkMatchesFilter (link, filterId, filterGroups) {
  if (!link) return false
  if (filterId === 'none') return false

  const category = getSkirtLinkCategory(link, filterGroups)
  if (filterId === 'weitere') return category === 'weitere'

  return category === filterId
}

export function getSkirtFilterForPreset (preset) {
  const mapping = guidedCheckoutConfig.skirtFilterByPreset || {}
  return mapping[preset] || 'fuxx'
}

export function getBundleLinkSelectionPrice (link) {
  const value = Number(link && link.price)
  return isNaN(value) ? 0 : value
}

/** Included in the set base price (no selection surcharge). */
export function isBundleLinkIncluded (link) {
  return getBundleLinkSelectionPrice(link) <= 0
}

/** Costs an extra surcharge on top of the set. */
export function isBundleLinkSurcharge (link) {
  return getBundleLinkSelectionPrice(link) > 0
}

/**
 * Filter skirt/underlay choices by package:
 * - einzelprodukt: all options
 * - standard: only free/included options
 * - premium: only surcharge options
 */
export function productLinkMatchesPackagePreset (link, preset = 'standard') {
  if (!link) return false
  if (preset === 'einzelprodukt') return true
  if (preset === 'premium') return isBundleLinkSurcharge(link)
  return isBundleLinkIncluded(link)
}

export function filterBundleLinksForPreset (links = [], preset = 'standard') {
  return (links || []).filter(link => productLinkMatchesPackagePreset(link, preset))
}

export function isEinzelbodenEnabled () {
  return Boolean(guidedCheckoutConfig.showEinzelboden)
}

export function getPackagePresetOptions () {
  const options = guidedCheckoutConfig.packageOptions || []
  const showEinzelboden = isEinzelbodenEnabled()

  return options.filter(option => !option.requiresEinzelboden || showEinzelboden)
}

export function getVisibleSteps (product, packagePreset = 'standard') {
  return guidedCheckoutConfig.steps.filter(step => {
    if (step.id === 'underlay') {
      return isBundleOptionSelectable(getUnderlayBundleOption(product))
    }

    if (step.id === 'skirt') {
      return isBundleOptionSelectable(getSkirtBundleOption(product))
    }

    if (step.id === 'accessories') {
      return hasRelatedAccessoryLinks(product)
    }

    return true
  })
}

export function getRelatedAccessorySkus (product) {
  const linkType = guidedCheckoutConfig.accessoryLinkType || 'related'
  const parentSku = product && product.sku

  return (product && product.product_links ? product.product_links : [])
    .filter(link => link && link.link_type === linkType)
    .map(link => {
      // VSF/Magento indexer format uses linked_product_sku; sku is the parent.
      if (link.linked_product_sku) return link.linked_product_sku
      // Fallback for partially synced docs where only the child sku was stored.
      if (link.sku && link.sku !== parentSku) return link.sku
      return null
    })
    .filter(Boolean)
}

export function hasRelatedAccessoryLinks (product) {
  return getRelatedAccessorySkus(product).length > 0
}

export function getAccessoryFilterGroups () {
  return guidedCheckoutConfig.accessoryFilterGroups || []
}

export function getAccessoryDisplayGroups () {
  return getAccessoryFilterGroups().filter(group => group.id && group.id !== 'all')
}

function getTypZubehoerValues (product) {
  const raw = product && product.typ_zubehoer
  if (raw == null || raw === '') return []
  const values = Array.isArray(raw) ? raw : [raw]
  return values.map(value => Number(value)).filter(value => !isNaN(value))
}

function nameIncludesKeyword (name, keywords = []) {
  if (!keywords.length) return false
  const normalized = (name || '').toLowerCase()
  return keywords.some(keyword => normalized.includes(String(keyword).toLowerCase()))
}

function categoryIsUnderRoot (category, rootId) {
  if (!category || rootId == null) return false
  if (Number(category.id) === Number(rootId)) return true
  const path = String(category.path || '')
  if (!path) return false
  return path.split('/').includes(String(rootId))
}

/**
 * Pick the deepest Magento category under the Zubehör root for grouping.
 */
export function getAccessoryGroupCategory (product, categoriesById = {}) {
  if (!product) return null

  const rootId = guidedCheckoutConfig.accessoryCategoryRootId || 10
  const exclude = new Set(
    (guidedCheckoutConfig.accessoryExcludeCategoryIds || []).map(id => Number(id))
  )
  const categoryIds = (product.category_ids || []).map(id => Number(id)).filter(id => !isNaN(id))

  let best = null
  for (const categoryId of categoryIds) {
    if (exclude.has(categoryId)) continue
    const category = categoriesById[categoryId] || categoriesById[String(categoryId)]
    if (!category || !categoryIsUnderRoot(category, rootId)) continue
    // Prefer leaf categories over the Zubehör root itself.
    if (Number(category.id) === Number(rootId)) continue
    if (exclude.has(Number(category.parent_id))) continue

    const level = Number(category.level) || 0
    const bestLevel = best ? (Number(best.level) || 0) : -1
    if (!best || level > bestLevel) {
      best = category
    }
  }

  return best
}

/**
 * Group related accessories by Magento category names (under Zubehör).
 * @param {object[]} products
 * @param {Record<string|number, object>} categoriesById
 */
export function groupAccessoryProducts (products = [], categoriesById = {}) {
  const buckets = new Map()

  for (const product of products || []) {
    if (!isEligibleAccessoryProduct(product)) continue

    const category = getAccessoryGroupCategory(product, categoriesById)
    const id = category ? String(category.id) : 'weitere'
    const label = category && category.name ? category.name : 'Weiteres Zubehör'

    if (!buckets.has(id)) {
      buckets.set(id, {
        id,
        label,
        categoryId: category ? Number(category.id) : null,
        products: []
      })
    }
    buckets.get(id).products.push(product)
  }

  const order = (guidedCheckoutConfig.accessoryCategoryOrder || []).map(id => String(id))
  const groups = Array.from(buckets.values()).filter(group => group.products.length > 0)

  groups.forEach(group => {
    group.products.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' }))
  })

  groups.sort((a, b) => {
    if (a.id === 'weitere') return 1
    if (b.id === 'weitere') return -1
    const indexA = order.indexOf(String(a.categoryId))
    const indexB = order.indexOf(String(b.categoryId))
    if (indexA !== -1 || indexB !== -1) {
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    }
    return String(a.label).localeCompare(String(b.label), 'de')
  })

  return groups
}

export function isEligibleAccessoryProduct (product) {
  if (!product) return false

  const excludeTyp = guidedCheckoutConfig.accessoryExcludeTypZubehoer || []
  const typValues = getTypZubehoerValues(product)
  if (typValues.some(value => excludeTyp.includes(value))) {
    return false
  }

  const excludeNames = guidedCheckoutConfig.accessoryExcludeNameKeywords || []
  if (nameIncludesKeyword(product.name, excludeNames)) {
    return false
  }

  return true
}

export function productMatchesAccessoryFilter (product, filterId, filterGroups = getAccessoryFilterGroups()) {
  if (!isEligibleAccessoryProduct(product)) return false
  if (!filterId || filterId === 'all') return true

  const filter = filterGroups.find(group => group.id === filterId)
  if (!filter) return true

  const name = product.name || ''
  if (nameIncludesKeyword(name, filter.nameKeywordsExclude || [])) {
    return false
  }

  const typValues = getTypZubehoerValues(product)
  const categoryIds = (product.category_ids || []).map(id => Number(id))
  const typMatch = (filter.typZubehoer || []).some(id => typValues.includes(Number(id)))
  const categoryMatch = (filter.categoryIds || []).some(id => categoryIds.includes(Number(id)))
  const nameMatch = nameIncludesKeyword(name, filter.nameKeywords || [])

  // Legacy rules kept for compatibility if custom filter groups are configured.
  if (filter.id === 'befestigung') {
    return nameMatch && (typMatch || !(filter.typZubehoer || []).length)
  }

  if (filter.id === 'leistenzubehoer') {
    return typMatch
  }

  if (filter.id === 'schienen') {
    return typMatch || categoryMatch
  }

  return typMatch || categoryMatch || nameMatch
}

export function getAccessoryUnitPrice (product) {
  if (!product) return 0
  const value = Number(
    product.final_price_incl_tax != null
      ? product.final_price_incl_tax
      : (product.price_incl_tax != null ? product.price_incl_tax : product.price)
  )
  return isNaN(value) ? 0 : value
}

export function getAccessoriesTotalAmount (selectedAccessories = []) {
  return selectedAccessories.reduce((sum, item) => {
    if (!item || !item.product) return sum
    const qty = Number(item.qty) || 0
    return sum + (getAccessoryUnitPrice(item.product) * qty)
  }, 0)
}

function getOptionLineAmount (selected) {
  if (!selected) return 0
  const unitPrice = Number(selected.price)
  if (isNaN(unitPrice)) return 0
  return unitPrice * (selected.qty || 0)
}

/**
 * Line items for the sticky guided-checkout bar.
 * @returns {{ id: string, label: string, amount: number|null, display?: string }[]}
 */
export function getGuidedCheckoutPriceLines (product, {
  fulfillmentMode = 'delivery',
  accessoriesAmount = null,
  accessories = []
} = {}) {
  const lines = []
  if (!product) return lines

  const productPrice = getProductPrice(product)
  const mainOption = getMainBundleOption(product)
  const mainValue = mainOption ? getSelectedBundleOptionValue(product, mainOption) : null
  const mainQty = (mainValue && mainValue.qty) || 1
  const floorUnit = productPrice.special || productPrice.regular || 0

  lines.push({
    id: 'floor',
    label: (mainValue && mainValue.product && mainValue.product.name) || 'Boden',
    amount: Number(floorUnit) * mainQty,
    product: (mainValue && mainValue.product) || null
  })

  const skirtOption = getSkirtBundleOption(product)
  if (skirtOption) {
    const skirtValue = getSelectedBundleOptionValue(product, skirtOption)
    if (skirtValue && skirtValue.product) {
      lines.push({
        id: 'skirt',
        label: skirtValue.product.name || 'Leiste',
        amount: getOptionLineAmount(skirtValue),
        product: skirtValue.product
      })
    } else {
      lines.push({
        id: 'skirt',
        label: 'Keine Leiste',
        amount: 0,
        product: null
      })
    }
  }

  const underlayOption = getUnderlayBundleOption(product)
  if (underlayOption) {
    const underlayValue = getSelectedBundleOptionValue(product, underlayOption)
    if (underlayValue && underlayValue.product) {
      lines.push({
        id: 'underlay',
        label: underlayValue.product.name || 'Dämmung',
        amount: getOptionLineAmount(underlayValue),
        product: underlayValue.product
      })
    } else {
      lines.push({
        id: 'underlay',
        label: 'Keine Dämmung',
        amount: 0,
        product: null
      })
    }
  }

  const accessoryItems = (accessories || []).filter(item => item && item.product && item.qty > 0)
  if (accessoryItems.length) {
    accessoryItems.forEach(item => {
      const unitPrice = getAccessoryUnitPrice(item.product)
      lines.push({
        id: `accessory-${item.product.sku}`,
        label: item.product.name || 'Zubehör',
        amount: unitPrice * item.qty,
        product: item.product
      })
    })
  } else {
    lines.push({
      id: 'accessories',
      label: 'Zubehör',
      amount: accessoriesAmount,
      display: accessoriesAmount == null ? '—' : undefined,
      product: null
    })
  }

  if (fulfillmentMode !== 'pickup') {
    lines.push({
      id: 'delivery',
      label: 'Lieferung',
      amount: null,
      display: 'zzgl. Versand'
    })
  }

  return lines
}

export function syncBundleOptionComponents (bundleOptionsComponent) {
  if (!bundleOptionsComponent || !bundleOptionsComponent.product) return

  for (const option of bundleOptionsComponent.product.bundle_options || []) {
    const optionComponent = getBundleOptionRef(bundleOptionsComponent, option)
    if (optionComponent && typeof optionComponent.bundleOptionChanged === 'function') {
      optionComponent.bundleOptionChanged()
    }
  }

  syncAdditionalBundleQuantities(bundleOptionsComponent)
}

function isValidBundleSelection (option, selectionId) {
  if (selectionId === null || selectionId === undefined || selectionId === 0 || selectionId === '0') {
    return false
  }

  return (option.product_links || []).some(link => String(link.id) === String(selectionId))
}

export function buildCompleteBundleOptions (product, bundleOptionsComponent = null): Record<number, SelectedBundleOption> {
  syncBundleOptionComponents(bundleOptionsComponent)

  const storedOptions = rootStore.state.product.current_bundle_options || {}
  const bundleOptions: Record<number, SelectedBundleOption> = {}

  for (const option of product.bundle_options || []) {
    const stored = storedOptions[option.option_id]
    const storedSelectionId = stored && stored.option_selections ? stored.option_selections[0] : null

    if (isValidBundleSelection(option, storedSelectionId)) {
      bundleOptions[option.option_id] = {
        option_id: option.option_id,
        option_qty: stored.option_qty || 1,
        option_selections: [Number(storedSelectionId)]
      }
      continue
    }

    if (!option.required) {
      continue
    }

    const defaultLink = findDefaultProductLink(option)
    if (!defaultLink) {
      continue
    }

    const defaultQty = typeof defaultLink.qty === 'string'
      ? parseInt(defaultLink.qty, 10)
      : (defaultLink.qty || 1)

    bundleOptions[option.option_id] = {
      option_id: option.option_id,
      option_qty: defaultQty,
      option_selections: [Number(defaultLink.id)]
    }
  }

  return bundleOptions
}

export async function prepareGuidedCheckoutProductForCart (product, bundleOptionsComponent = null) {
  const bundleOptions = buildCompleteBundleOptions(product, bundleOptionsComponent)

  await rootStore.dispatch('product/setBundleOptions', {
    product,
    bundleOptions
  }, { root: true })

  return rootStore.getters['product/getCurrentProduct']
}
